/**
 * ライセンス管理（Excel）画面（License_Grid）の純粋ロジック
 *
 * 画面コンポーネント（`LicenseSpreadsheetPage.tsx`）から呼ばれる副作用のない関数のみを
 * ここに置く。React hooks / DOM 操作 / jspreadsheet インスタンス操作は**含めない**。
 * 過去に重大バグを起こした箇所（Orig_Index とフィールドの対応、空行スキップ、dirty 追跡、
 * 行index↔ID の同期）を property-based test で守るための切り出しである。
 *
 * 列インデックスの用語:
 *   - Orig_Index    : COLUMNS 配列上の固定インデックス（保存・cellMeta・フィルタの基準）
 *   - Display_Order : ユーザーの並べ替えを反映した表示順。`order[displayIdx] = origIdx`
 */

import {
  displayToOriginalRow,
  originalToDisplayRow,
  normalizeColumnOrder,
  sanitizeHiddenColumns,
  toIsoDateOrNull,
  toNumberOrNull,
} from '../utils/licenseCellMeta';

// 既存実装を再利用する（再実装しない）。画面側は本モジュール経由でも参照できるよう re-export する。
export {
  displayToOriginalRow,
  originalToDisplayRow,
  normalizeColumnOrder,
  sanitizeHiddenColumns,
  toIsoDateOrNull,
  toNumberOrNull,
};

// ============================================================
// 列定義（Orig_Index 固定）
// ============================================================

export interface LicenseColumnDef {
  title: string;
  width: number;
}

export const COLUMNS: LicenseColumnDef[] = [
  { title: '顧客', width: 150 }, // 0  必須・固定列・dropdown
  { title: 'ライセンス名', width: 220 }, // 1  必須・固定列・text(left)
  { title: '購入日', width: 110 }, // 2  calendar
  { title: '注文番号', width: 130 }, // 3  text(left)
  { title: '製品認証キー', width: 200 }, // 4  text(left)
  { title: 'ライセンスID', width: 160 }, // 5  text(left)
  { title: 'Lic数', width: 90 }, // 6  numeric(right)
  { title: '割当数', width: 90 }, // 7  numeric(right)
  { title: '導入日', width: 110 }, // 8  calendar
  { title: '前回更新日', width: 110 }, // 9  calendar
  { title: '備考', width: 240 }, // 10 text(left)
  { title: '次回更新日', width: 110 }, // 11 calendar
  { title: '管理者', width: 120 }, // 12 dropdown
  { title: '通知日数', width: 80 }, // 13 numeric(right)
];

/** 常時左端に固定表示する列（Display_Order の先頭2位置に固定する） */
export const FIXED_COLUMNS: number[] = [0, 1];
/** カレンダー型セルの列（Orig_Index） */
export const DATE_COLUMNS: number[] = [2, 8, 9, 11];
/** 数値型セル（右寄せ）の列（Orig_Index） */
export const NUMERIC_COLUMNS: number[] = [6, 7, 13];
/**
 * 左寄せ表示の列（Orig_Index）。顧客(0) はドロップダウン型だが本文セルは左寄せにする
 * （資産台帳と揃えるため）。列タイトルは中央寄せのまま（`align` は本文セルにのみ効き、
 * ヘッダーは画面側の `thead td { text-align: center }` で中央寄せに固定される）。
 */
export const LEFT_ALIGN_COLUMNS: number[] = [0, 1, 3, 4, 5, 10, 12];

/** 日付ピッカーの日本語表示設定（`options.format` は表示書式 `YYYY/MM/DD`） */
const CALENDAR_OPTIONS = {
  format: 'YYYY/MM/DD',
  months: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'],
  weekdays: ['日', '月', '火', '水', '木', '金', '土'],
  weekdays_short: ['日', '月', '火', '水', '木', '金', '土'],
} as const;

/**
 * jspreadsheet の `columns` オプションを生成する。
 *
 * 型（dropdown / calendar / numeric / text）は**表示位置ではなく `order[displayIdx]` が示す
 * Orig_Index** を見て割り当てる。列を並べ替えても各列の型・書式が追従するようにするため。
 *
 * @param order         Display_Order → Orig_Index の対応（`order[displayIdx] = origIdx`）
 * @param customerNames 「顧客」列ドロップダウンの選択肢（Customer_Map のキー）
 * @param isAdmin       Admin_User なら編集可、Viewer_User なら全列 readOnly（要件 7.12）
 * @param savedWidths   LocalStorage に保存済みの列幅（**表示位置基準**）。無ければ既定幅を使う
 * @param managerNames  「管理者」列ドロップダウンの選択肢（User名一覧）
 */
export function buildColumnsConfig(
  order: number[],
  customerNames: string[],
  isAdmin: boolean,
  savedWidths?: number[] | null,
  managerNames?: string[],
): Record<string, unknown>[] {
  return order.map((origIdx, displayIdx) => {
    const col = COLUMNS[origIdx];
    const base: Record<string, unknown> = {
      title: col?.title ?? '',
      width: savedWidths?.[displayIdx] ?? col?.width ?? 100,
      readOnly: !isAdmin,
    };
    if (origIdx === 0) {
      // 顧客: 登録済み顧客名のドロップダウン（オートコンプリート付き）
      base.type = 'dropdown';
      base.source = customerNames;
      base.autocomplete = true;
    } else if (origIdx === 12) {
      // 管理者: ユーザー名のドロップダウン（オートコンプリート付き）
      base.type = 'dropdown';
      base.source = managerNames ?? [];
      base.autocomplete = true;
    } else if (DATE_COLUMNS.includes(origIdx)) {
      base.type = 'calendar';
      base.options = { ...CALENDAR_OPTIONS };
    } else if (NUMERIC_COLUMNS.includes(origIdx)) {
      // 桁区切り表示 + 右寄せ。内部値は数値のまま保持される
      base.type = 'numeric';
      base.mask = '#,##0';
      base.align = 'right';
    }
    if (LEFT_ALIGN_COLUMNS.includes(origIdx)) {
      // jspreadsheet の既定は中央寄せなので明示的に左寄せにする
      base.align = 'left';
    }
    return base;
  });
}

/**
 * 列移動 `(from → to)` を適用したうえで、固定2列（顧客=0, ライセンス名=1）を
 * Display_Order の先頭2位置へ戻した列順を返す（要件 5.7）。
 *
 * 純粋関数。実際の `instance.moveColumn` 呼び出しは画面側（`onmovecolumn`）で行う。
 * 戻り値は常に 0〜COLUMNS.length-1 をちょうど1回ずつ含み、`[0, 1, ...]` で始まる。
 */
export function snapBackOrder(order: number[], from: number, to: number): number[] {
  const next = [...order];
  if (from >= 0 && from < next.length) {
    const [moved] = next.splice(from, 1);
    const target = Math.max(0, Math.min(to, next.length));
    next.splice(target, 0, moved);
  }
  // 固定列を先頭から順に定位置へスナップバックする
  for (let fixedPos = 0; fixedPos < FIXED_COLUMNS.length; fixedPos++) {
    const fixedIdx = FIXED_COLUMNS[fixedPos];
    const current = next.indexOf(fixedIdx);
    if (current !== -1 && current !== fixedPos) {
      next.splice(current, 1);
      next.splice(fixedPos, 0, fixedIdx);
    }
  }
  return next;
}

// ============================================================
// 顧客名 → 顧客ID の解決 / 保存 payload の組み立て
// ============================================================

export interface CustomerRef {
  id: string;
  name: string;
}

/** 顧客名 → 顧客 の対応表（Customer_Map） */
export type CustomerMap = Map<string, CustomerRef>;

/** 顧客名から顧客IDを解決する。未登録名・空名は null（要件 6.3, 6.4）。 */
export function resolveCustomerId(customerMap: CustomerMap, name: string | null | undefined): string | null {
  const trimmed = String(name ?? '').trim();
  if (!trimmed) return null;
  return customerMap.get(trimmed)?.id ?? null;
}

export type LicenseCellValue = string | number | null | undefined;

export interface ContentPayload {
  customerId: string | null;
  licenseName: string;
  purchaseDate: string | null;
  orderNumber: string | null;
  productKey: string | null;
  licenseId: string | null;
  licenseCount: number | null;
  assignedCount: number | null;
  installDate: string | null;
  lastRenewalDate: string | null;
  nextRenewalDate: string | null;
  managerId: string | null;
  notificationDaysOverride: number | null;
  notes: string | null;
}

export type BuildContentPayloadResult = { payload: ContentPayload } | { error: string };

/**
 * Orig_Index 順の行配列から保存 payload を組み立てる（要件 6.7, 6.8）。
 *
 * 呼び出し側は**必ず先に `displayToOriginalRow` で Orig_Index 順へ逆マッピングしてから**
 * 渡すこと。以後は `originalRow[origIdx]` のみを参照するため、Display_Order に依存しない。
 *
 * 顧客名が Customer_Map に無い場合は `{ error: 顧客名 }` を返し、呼び出し側が
 * 行番号付きのエラーメッセージを表示する（要件 6.4）。
 */
/**
 * ドロップダウンセルの生値を表示ラベルへ解決する。
 *
 * jspreadsheet-ce は、ユーザーがドロップダウンから選択した時点でセルの値を
 * `source` 配列の**インデックス**として保持する。一方、初期表示で流し込む
 * データはラベル文字列そのままである。そのため `getData()` は「未操作の行は
 * ラベル」「ユーザーが変更した行は数値インデックス」を返す。インデックスを
 * そのまま Customer_Map で引くと解決できず、顧客を変更して保存すると顧客が
 * 空になる／保存失敗になる。ここで両方の形を正規化する。
 */
export function resolveDropdownLabel(raw: LicenseCellValue, source: string[]): string {
  const value = String(raw ?? '').trim();
  if (value === '') return '';
  if (/^\d+$/.test(value)) {
    const byIndex = source[Number(value)];
    if (byIndex !== undefined) return byIndex;
  }
  return value;
}

export function buildContentPayload(
  originalRow: LicenseCellValue[],
  customerMap: CustomerMap,
  /** 顧客ドロップダウンの選択肢。省略時は生値をそのままラベルとして扱う。 */
  customerSource: string[] = [],
  /** 管理者ドロップダウンの選択肢。省略時は生値をそのままラベルとして扱う。 */
  managerSource: string[] = [],
  /** 管理者名→ID の対応表。 */
  managerMap?: Map<string, { id: string; name: string }>,
): BuildContentPayloadResult {
  const customerName = resolveDropdownLabel(originalRow[0], customerSource);
  const customer = customerName ? customerMap.get(customerName) : undefined;
  if (customerName && !customer) return { error: customerName };
  const text = (value: LicenseCellValue): string | null => {
    const raw = String(value ?? '').trim();
    return raw === '' ? null : raw;
  };
  const managerName = resolveDropdownLabel(originalRow[12], managerSource);
  const managerId = managerName && managerMap ? managerMap.get(managerName)?.id ?? null : null;
  return {
    payload: {
      customerId: customer?.id ?? null, // Orig 0
      licenseName: String(originalRow[1] ?? '').trim(), // Orig 1
      purchaseDate: toIsoDateOrNull(originalRow[2]), // Orig 2
      orderNumber: text(originalRow[3]), // Orig 3
      productKey: text(originalRow[4]), // Orig 4
      licenseId: text(originalRow[5]), // Orig 5
      licenseCount: toNumberOrNull(originalRow[6]), // Orig 6
      assignedCount: toNumberOrNull(originalRow[7]), // Orig 7
      installDate: toIsoDateOrNull(originalRow[8]), // Orig 8
      lastRenewalDate: toIsoDateOrNull(originalRow[9]), // Orig 9
      notes: text(originalRow[10]), // Orig 10
      nextRenewalDate: toIsoDateOrNull(originalRow[11]), // Orig 11
      managerId, // Orig 12
      notificationDaysOverride: toNumberOrNull(originalRow[13]), // Orig 13
    },
  };
}

// ============================================================
// Dirty 追跡 → 発行リクエストの分類
// ============================================================

/** 新規行のプレースホルダID接頭辞（保存時は常に POST 対象になる） */
export const NEW_ROW_ID_PREFIX = '__new_';

/** 行に対して発行すべきリクエスト種別 */
export type SaveRequestKind = 'post' | 'put' | 'patch' | 'both' | 'skip';

export interface RowSavePlan {
  /** グリッドの行インデックス */
  index: number;
  /** 行に紐付く License_Record の ID（新規行はプレースホルダID） */
  rowId: string;
  kind: SaveRequestKind;
}

/** 未保存の新規行（ID未確定）かどうか。 */
export function isNewRowId(rowId: string | null | undefined): boolean {
  return !rowId || rowId.startsWith(NEW_ROW_ID_PREFIX);
}

/**
 * 行IDリストと dirty 集合から、各行に発行すべきリクエスト種別を決定する
 * （要件 9.1, 9.2, 9.5, 9.6）。
 *
 * - 新規行 → `'post'`（内容と cellMeta を1リクエストで送る）
 * - 既存行で内容のみ dirty → `'put'`
 * - 既存行で cellMeta のみ dirty → `'patch'`
 * - 既存行で両方 dirty → `'both'`（PUT と PATCH の両方を発行する）
 * - dirty でない既存行 → `'skip'`
 *
 * dirty 集合は行インデックスではなく **ID 単位**であるため、行の並べ替えが起きても
 * 分類結果（ID の集合）は変わらない。
 */
export function classifyDirtyRows(
  rowIds: (string | null | undefined)[],
  dirtyContentIds: Iterable<string>,
  dirtyMetaIds: Iterable<string>,
): RowSavePlan[] {
  const contentSet = new Set(dirtyContentIds);
  const metaSet = new Set(dirtyMetaIds);
  return rowIds.map((rawId, index) => {
    const rowId = rawId ?? '';
    if (isNewRowId(rowId)) return { index, rowId, kind: 'post' as SaveRequestKind };
    const contentDirty = contentSet.has(rowId);
    const metaDirty = metaSet.has(rowId);
    let kind: SaveRequestKind = 'skip';
    if (contentDirty && metaDirty) kind = 'both';
    else if (contentDirty) kind = 'put';
    else if (metaDirty) kind = 'patch';
    return { index, rowId, kind };
  });
}

/** `PUT /licenses/:id` を発行する対象ID（`'put'` と `'both'`）。 */
export function putTargetIds(plans: RowSavePlan[]): string[] {
  return plans.filter((p) => p.kind === 'put' || p.kind === 'both').map((p) => p.rowId);
}

/** `PATCH /licenses/:id/cell-meta` を発行する対象ID（`'patch'` と `'both'`）。 */
export function patchTargetIds(plans: RowSavePlan[]): string[] {
  return plans.filter((p) => p.kind === 'patch' || p.kind === 'both').map((p) => p.rowId);
}

// ============================================================
// 行index ↔ ID の同期（挿入・削除・並べ替え / Undo・Redo）
// ============================================================

export type RowIdOperation =
  /** `position` の位置に `newIds` を挿入する（行追加） */
  | { type: 'insert'; position: number; newIds: string[] }
  /** `position` から `count` 行分の ID を取り除く（行削除） */
  | { type: 'delete'; position: number; count: number }
  /** `order[newIdx] = 旧インデックス` の順列を適用する（ソート） */
  | { type: 'reorder'; order: number[] };

/**
 * 行ID配列に対して挿入・削除・並べ替えを適用する（要件 9.11, 9.12）。
 * 元配列は変更せず、新しい配列を返す。
 */
export function applyRowIdOperation(ids: string[], op: RowIdOperation): string[] {
  switch (op.type) {
    case 'insert': {
      const position = Math.max(0, Math.min(op.position, ids.length));
      return [...ids.slice(0, position), ...op.newIds, ...ids.slice(position)];
    }
    case 'delete': {
      const position = Math.max(0, Math.min(op.position, ids.length));
      const count = Math.max(0, Math.min(op.count, ids.length - position));
      return [...ids.slice(0, position), ...ids.slice(position + count)];
    }
    case 'reorder':
      return op.order.map((prevIdx) => ids[prevIdx]);
    default:
      return [...ids];
  }
}

/**
 * 操作を打ち消す逆操作を返す（Undo 用）。
 * `ids` は**その操作を適用する直前**の行ID配列であること。
 */
export function invertRowIdOperation(ids: string[], op: RowIdOperation): RowIdOperation {
  switch (op.type) {
    case 'insert': {
      const position = Math.max(0, Math.min(op.position, ids.length));
      return { type: 'delete', position, count: op.newIds.length };
    }
    case 'delete': {
      const position = Math.max(0, Math.min(op.position, ids.length));
      const count = Math.max(0, Math.min(op.count, ids.length - position));
      return { type: 'insert', position, newIds: ids.slice(position, position + count) };
    }
    case 'reorder': {
      // 逆順列: inverse[order[i]] = i
      const inverse: number[] = [];
      op.order.forEach((prevIdx, newIdx) => {
        inverse[prevIdx] = newIdx;
      });
      return { type: 'reorder', order: inverse };
    }
    default:
      return op;
  }
}

// ============================================================
// 空行スキップ判定
// ============================================================

/**
 * 新規行が保存対象外（入力途中の空行）かどうかを判定する（要件 9.7, 9.8）。
 *
 * 新規行かつ Orig_Index 0（顧客）と 1（ライセンス名）の**双方**が空文字列または
 * 空白文字のみのときだけ true。いずれか一方に値があれば保存対象に含め、
 * 必須項目エラーはサーバー側に返させる。既存行にはこの判定を適用しない。
 */
export function shouldSkipNewRow(originalRow: LicenseCellValue[], isNewRow: boolean): boolean {
  if (!isNewRow) return false;
  const customerName = String(originalRow[0] ?? '').trim();
  const licenseName = String(originalRow[1] ?? '').trim();
  return customerName === '' && licenseName === '';
}

// ============================================================
// 残ライセンス数（超過）の判定
// ============================================================

export interface OverAssignedInput {
  licenseCount: LicenseCellValue;
  assignedCount: LicenseCellValue;
}

export interface OverAssignedResult {
  /** 行ごとの超過判定（表示順） */
  flags: boolean[];
  /** 超過と判定された行数（ステータスバー表示用） */
  count: number;
}

/**
 * 各行の (Lic数, 割当数) から超過行を判定する（要件 11.1, 11.2, 11.3）。
 * 双方が非 null かつ `割当数 > Lic数` のときのみ超過。どちらかが空なら警告なし。
 */
export function evaluateOverAssigned(rows: OverAssignedInput[]): OverAssignedResult {
  const flags = rows.map(({ licenseCount, assignedCount }) => {
    const lic = toNumberOrNull(licenseCount);
    const assigned = toNumberOrNull(assignedCount);
    return lic !== null && assigned !== null && assigned > lic;
  });
  return { flags, count: flags.filter(Boolean).length };
}

// ============================================================
// 必須項目の未入力強調
// ============================================================

/**
 * 必須入力の列（Orig_Index）。顧客(0) と ライセンス名(1)。
 * 未入力（空文字列または空白のみ）のセルだけをオレンジ枠で強調する。
 */
export const REQUIRED_COLUMNS: number[] = [0, 1];

/** 未入力強調に使う枠線色（プロジェクト共通の警告色）。 */
export const REQUIRED_HIGHLIGHT_COLOR = '#ed6c02';

/** 値が未入力（空文字列または空白のみ）かどうか。 */
export function isBlankValue(value: LicenseCellValue): boolean {
  return String(value ?? '').trim() === '';
}

/** 必須列の Orig_Index → Display_Order 対応（グリッドに存在しない列は除外）。 */
export interface RequiredHighlightTarget {
  origIdx: number;
  displayIdx: number;
}

/**
 * 必須列（Orig_Index）を現在の列順に基づく表示位置へ変換する。
 * `order` に含まれない列（想定外）は対象外として除外する。純粋関数。
 */
export function requiredHighlightTargets(
  order: number[],
  required: number[] = REQUIRED_COLUMNS,
): RequiredHighlightTarget[] {
  return required
    .map((origIdx) => ({ origIdx, displayIdx: order.indexOf(origIdx) }))
    .filter((t) => t.displayIdx !== -1);
}

/**
 * 表示位置ごとの値配列から、必須未入力セルの表示位置を返す（純粋関数）。
 * DOM 操作は行わない。画面側がこの結果に対して outline を当てる。
 *
 * @param getValue    `(displayIdx) => セル値` を返す関数
 * @param order       Display_Order → Orig_Index の対応
 */
export function blankRequiredDisplayIdxs(
  getValue: (displayIdx: number) => LicenseCellValue,
  order: number[],
  required: number[] = REQUIRED_COLUMNS,
): number[] {
  return requiredHighlightTargets(order, required)
    .filter(({ displayIdx }) => isBlankValue(getValue(displayIdx)))
    .map(({ displayIdx }) => displayIdx);
}
